<template>
  <div class="page-container">
    <div class="page-header">
      <div class="header-left">
        <button class="btn btn-secondary" @click="handleBack">
          <i class="ri-arrow-left-line"></i>
          返回
        </button>
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions" v-if="isSystemAccount">
        <select v-model="selectedPlatformId" @change="handlePlatformChange" class="form-control">
          <option value="">全部平台</option>
          <option v-for="platform in platforms" :key="platform.id" :value="platform.id">
            {{ platform.name }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchOverview">重試</button>
    </div>

    <div v-else>
      <!-- 統計卡片 -->
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-label">總客戶數</div>
          <div class="stat-value">{{ overviewData?.summary?.totalClients || 0 }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">總應收金額</div>
          <div class="stat-value">NT$ {{ formatCurrency(overviewData?.summary?.totalReceivable || 0) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">總已收金額</div>
          <div class="stat-value">NT$ {{ formatCurrency(overviewData?.summary?.totalReceived || 0) }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-label">未收金額</div>
          <div class="stat-value">NT$ {{ formatCurrency((overviewData?.summary?.totalReceivable || 0) - (overviewData?.summary?.totalReceived || 0)) }}</div>
        </div>
      </div>

      <!-- 帳單狀態分布 -->
      <div class="status-section">
        <h2 class="section-title">帳單狀態分布</h2>
        <div class="status-grid">
          <div class="status-card normal">
            <div class="status-header">
              <span class="status-badge normal">正常</span>
              <span class="status-count">{{ overviewData?.statusBreakdown?.normal?.count || 0 }} 客戶</span>
            </div>
            <div class="status-amount">NT$ {{ formatCurrency(overviewData?.statusBreakdown?.normal?.totalAmount || 0) }}</div>
          </div>
          <div class="status-card due-soon">
            <div class="status-header">
              <span class="status-badge due-soon">即將到期</span>
              <span class="status-count">{{ overviewData?.statusBreakdown?.dueSoon?.count || 0 }} 客戶</span>
            </div>
            <div class="status-amount">NT$ {{ formatCurrency(overviewData?.statusBreakdown?.dueSoon?.totalAmount || 0) }}</div>
          </div>
          <div class="status-card grace-period">
            <div class="status-header">
              <span class="status-badge grace-period">寬限期</span>
              <span class="status-count">{{ overviewData?.statusBreakdown?.gracePeriod?.count || 0 }} 客戶</span>
            </div>
            <div class="status-amount">NT$ {{ formatCurrency(overviewData?.statusBreakdown?.gracePeriod?.totalAmount || 0) }}</div>
          </div>
          <div class="status-card expired">
            <div class="status-header">
              <span class="status-badge expired">已過期</span>
              <span class="status-count">{{ overviewData?.statusBreakdown?.expired?.count || 0 }} 客戶</span>
            </div>
            <div class="status-amount">NT$ {{ formatCurrency(overviewData?.statusBreakdown?.expired?.totalAmount || 0) }}</div>
          </div>
        </div>
      </div>

      <!-- 快速導航 -->
      <div class="quick-links-section">
        <h2 class="section-title">快速導航</h2>
        <div class="quick-links">
          <router-link to="/system/manual-payments/due-soon" class="quick-link">
            <i class="ri-time-line"></i>
            <span>即將到期款項</span>
          </router-link>
          <router-link to="/system/manual-payments/grace-period" class="quick-link">
            <i class="ri-alarm-warning-line"></i>
            <span>寬限期款項</span>
          </router-link>
          <router-link to="/system/manual-payments/overdue" class="quick-link">
            <i class="ri-error-warning-line"></i>
            <span>超過寬限期未收款</span>
          </router-link>
          <router-link to="/system/subscription-clients" class="quick-link">
            <i class="ri-user-line"></i>
            <span>訂閱客戶管理</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { billingService } from '@/api/modules/billing.js';
import { platformManagementService } from '@/api/modules/platforms.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';

export default {
  name: 'BillingOverview',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('帳單總覽');

    const loading = ref(false);
    const error = ref(null);
    const overviewData = ref(null);
    const platforms = ref([]);
    const selectedPlatformId = ref('');

    const isSystemAccount = computed(() => {
      return authService.getAccountType() === 'system';
    });

    const fetchPlatforms = async () => {
      if (!isSystemAccount.value) return;
      try {
        const response = await platformManagementService.getPlatforms();
        if (response.success) {
          platforms.value = response.data.items || [];
        }
      } catch (err) {
        console.error('取得平台列表失敗:', err);
      }
    };

    const fetchOverview = async () => {
      loading.value = true;
      error.value = null;
      try {
        const params = {};
        if (isSystemAccount.value && selectedPlatformId.value) {
          params.platformId = selectedPlatformId.value;
        }
        const response = await billingService.getBillingOverview(params);
        if (response.success) {
          overviewData.value = response.data;
        } else {
          error.value = response.message || '取得帳單總覽失敗';
        }
      } catch (err) {
        error.value = err.response?.data?.message || err.message || '取得帳單總覽失敗';
        showError(error.value);
      } finally {
        loading.value = false;
      }
    };

    const handlePlatformChange = () => {
      fetchOverview();
    };

    const formatCurrency = (value) => {
      return new Intl.NumberFormat('zh-TW').format(value);
    };

    const handleBack = () => {
      router.push('/system/billing');
    };

    onMounted(() => {
      fetchPlatforms();
      fetchOverview();
    });

    return {
      pageTitle,
      loading,
      error,
      overviewData,
      platforms,
      selectedPlatformId,
      isSystemAccount,
      fetchOverview,
      handlePlatformChange,
      formatCurrency,
      handleBack
    };
  }
};
</script>

<style scoped>
.page-container {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.page-title {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 32px;
}

.stat-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
}

.status-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 16px;
}

.status-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.status-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
}

.status-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.normal {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.due-soon {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.grace-period {
  background: #fde68a;
  color: #78350f;
}

.status-badge.expired {
  background: #fee2e2;
  color: #991b1b;
}

.status-count {
  font-size: 14px;
  color: #6b7280;
}

.status-amount {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
}

.quick-links-section {
  margin-bottom: 32px;
}

.quick-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.quick-link {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  text-decoration: none;
  color: #1a1a1a;
  transition: all 0.2s;
}

.quick-link:hover {
  border-color: #3b82f6;
  color: #3b82f6;
}

.quick-link i {
  font-size: 20px;
  margin-right: 12px;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 48px;
}

.spinner {
  border: 3px solid #f3f4f6;
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc2626;
  margin-bottom: 16px;
}
</style>
